// Silence this error as we want to explicitly extend the message descriptor
// from react-intl-next
// eslint-disable-next-line no-restricted-imports
import { type MessageDescriptor as ReactIntlMsgDescriptor } from 'react-intl-next';
export enum Product {
  WHITEBOARDS = 'whiteboards',
  DATABASES = 'databases',
}

/**
 * Extends the react-intl-next MessageDescriptor to make all fields non-nullable
 * since we always want to provide a value for each field. This gives us better
 * type safety guarantees when using the MessageDescriptor and prevents excessive
 * type-checks when using the MessageDescriptor.
 */
export interface MessageDescriptor extends ReactIntlMsgDescriptor {
  id: NonNullable<ReactIntlMsgDescriptor['id']>;
  defaultMessage: NonNullable<ReactIntlMsgDescriptor['defaultMessage']>;
  description: NonNullable<ReactIntlMsgDescriptor['description']>;
}

export interface StringForTranslation {
  defaultMessage: string;
  description: string;
}

export type ExternalMessageLoader = () => Promise<{
  // Since we don't get message types, we need to declare that the .default export contains the messages
  // from the external dependency import
  default: Record<string, string>;
}>;
export type ExternalMessageLoaders = Record<
  // The key, eg "editor"
  string,
  // An array of the loader functions that will return the messages for that key
  ExternalMessageLoader[]
>;

/**
 * To map between short-form locale keys to full-form, e.g. 'fr' -> 'fr-FR'
 */
export const localeMap = {
  en: 'en',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
  de: 'de-DE',
  cs: 'cs-CZ',
  ko: 'ko-KR',
  'pt-BR': 'pt-BR',
  fr: 'fr-FR',
  es: 'es',
  it: 'it-IT',
  ja: 'ja-JP',
  nl: 'nl-NL',
  nb: 'nb-NO',
  da: 'da-DK',
  sv: 'sv-SE',
  fi: 'fi-FI',
  ru: 'ru-RU',
  pl: 'pl-PL',
  tr: 'tr-TR',
  hu: 'hu-HU',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
} as const;

// When adding a new locale, make sure to add it to this array
export const i18nLocales = [
  'en',
  'en-GB',
  'en-US',
  'de',
  'cs',
  'ko',
  // ATM pt-PT is deprecated but no decision has been made whether to swap users over to pt-BR,
  // so we'll fall back to en-US for it.
  // Nonetheless, we support pt-BR (brasilian portuguese) natively
  'pt-BR',
  'fr',
  'es',
  'it',
  'ja',
  'nl',
  'nb',
  'da',
  'sv',
  'fi',
  'ru',
  'pl',
  'tr',
  'hu',
  'zh-CN',
  'zh-TW',
] as const;

export type ShortLocale = (typeof i18nLocales)[number];
export type LongLocale = (typeof localeMap)[ShortLocale];

export const debugLocales = [
  // Debug locales
  'en-pseudo',
  'xx',
  'en-ZZ',
] as const;

export const supportedLocale = [...i18nLocales, ...debugLocales] as const;

export type SupportedLocale = (typeof supportedLocale)[number];

export type MessageFormatter = (
  message: MessageDescriptor,
  values?: Record<string, string>
) => string;
